import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { Options } from 'flatpickr/dist/types/options';
import { German } from 'flatpickr/dist/l10n/de';
import { French } from 'flatpickr/dist/l10n/fr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { english } from 'flatpickr/dist/l10n/default';
import render from '@tk/utilities/tk.render';

type DatePickerMode = 'multiple' | 'range' | 'single';

type DatePickerMonthSelectorType = 'dropdown' | 'static';

enum DatePickerLocale {
    'de' = 1,
    'fr',
    'it',
    'en',
}

type DatePickerLocaleKey = keyof typeof DatePickerLocale;

type DatePickerRangeItem = {
    from: string;
    to: string;
};

export default class TKFormDatepicker extends TKCustomElementFactory {
    fieldElement?: HTMLInputElement;
    iconElement?: HTMLSpanElement;
    labelElement?: HTMLLabelElement;
    dateRangePicker?: Instance;
    calendarHeight: number;
    hideWeekNumbers: boolean;
    minDate?: string;
    maxDate?: string;
    mode: DatePickerMode;
    localeKey: DatePickerLocaleKey;
    monthSelectorType: DatePickerMonthSelectorType;
    dateFormat: string;
    conjunction: string;
    defaultDate: string[] | null;
    allowMobile: boolean;
    disableDates: string[];
    disableDatesRange?: DatePickerRangeItem[];
    actionButtons: HTMLButtonElement[];

    constructor() {
        super();

        this.fieldElement = this.querySelector('input') || undefined;
        this.iconElement = this.querySelector('[data-tk-addon]') || undefined;
        this.labelElement = this.querySelector('label') || undefined;
        this.hideWeekNumbers = this.hasAttribute('data-tk-hide-week-numbers');
        this.minDate = this.getAttribute('data-tk-min-date')!;
        this.maxDate = this.getAttribute('data-tk-max-date')!;
        this.mode = (this.getAttribute('data-tk-mode') || 'single') as DatePickerMode;
        this.localeKey = (this.getAttribute('data-tk-locale') || DatePickerLocale[1]) as DatePickerLocaleKey;
        this.monthSelectorType = (this.getAttribute('data-tk-month-selector-type')
            || 'dropdown') as DatePickerMonthSelectorType;
        this.dateFormat = this.getAttribute('data-tk-date-format') || 'd.m.Y';
        this.conjunction = this.getAttribute('data-tk-conjunction') || ',';
        this.defaultDate = this.getAttribute('data-tk-default-date')?.split(';') || null;
        this.allowMobile = this.hasAttribute('data-tk-allow-mobile');
        this.disableDates = this.getAttribute('data-tk-disable-dates')?.split(';') || [];
        if (this.getAttribute('data-tk-disable-dates-range')) {
            this.disableDatesRange = JSON.parse(this.getAttribute('data-tk-disable-dates-range')!);
        }
        this.actionButtons = [];
        this.calendarHeight = 0;
    }

    connectedCallback() {
        if (!this.fieldElement) return;
        TKFormDatepicker.initLanguages();
        this.dateRangePicker = flatpickr(this.fieldElement, {
            weekNumbers: !this.hideWeekNumbers,
            dateFormat: this.dateFormat,
            minDate: this.minDate,
            maxDate: this.maxDate,
            mode: this.mode,
            locale: this.localeKey,
            monthSelectorType: this.monthSelectorType,
            conjunction: this.conjunction,
            defaultDate: this.defaultDate,
            disableMobile: !this.allowMobile,
            disable: this.disableDatesRange ? this.disableDatesRange : this.disableDates,
            onReady(selectedDates: string[], dateStr: string, instance: Instance) {
                const calendarElement: HTMLElement = instance.calendarContainer;
                calendarElement.classList.add('closed');
            },
            onChange(selectedDates: string[], dateStr: string, instance: Instance) {
                const calendarElement: HTMLElement = instance.calendarContainer;

                // Required to prevent close and open at the same time
                const missingClassClosed = !calendarElement.classList.contains('open')
                && !calendarElement.classList.contains('closed');

                if (missingClassClosed) {
                    calendarElement.classList.add('closed');
                }
            },
            onValueUpdate: this.removeIgnoreField.bind(this),
        } as unknown as Options);

        this.createButtonsArea();
        this.registerActionButtonsClickListener();
        this.getCalendarHeight();

        this.registerClickListener();
    }

    registerClickListener() {
        if (!this.iconElement) throw new Error('Missing calendar icon');
        if (!this.fieldElement) throw new Error('Missing input element');
        if (!this.labelElement) throw new Error('Missing label element');

        const clickedHandler = this.onClicked.bind(this);
        const documentClickedHandler = this.onDocumentClick.bind(this);
        this.pushListener({ event: 'click', element: this.iconElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: this.fieldElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: this.labelElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: document, action: documentClickedHandler });
    }

    onClicked(event: MouseEvent) {
        if (!this.dateRangePicker) return;

        // Necessary so that the default behavior is suppressed when clicking on the label
        if ((event.target instanceof HTMLLabelElement) && (event.target.closest('tk-form-datepicker'))) {
            event.preventDefault();
        }
        const { calendarContainer } = this.dateRangePicker;

        const isOpen = !calendarContainer.classList.contains('closed');

        const absolutePos = this.getAbsolutePosition(this.fieldElement as HTMLElement);

        if (isOpen) {
            setTimeout(() => {
                calendarContainer.classList.add('closed');
                calendarContainer.classList.remove('open');
            }, 200);
        } else {
            calendarContainer.classList.remove('closed');
            calendarContainer.classList.add('open');
            calendarContainer.style.width = '100%';
            calendarContainer.style.top = absolutePos.top;
            calendarContainer.style.left = absolutePos.left;
        }
    }

    static initLanguages() {
        flatpickr.localize(German);
        flatpickr.localize(French);
        flatpickr.localize(Italian);
        flatpickr.localize(english);
    }

    removeIgnoreField() {
        this.fieldElement?.removeAttribute('data-tk-ignore-field');
    }

    createButtonsArea() {
        if (!this.dateRangePicker) return;

        const { calendarContainer } = this.dateRangePicker;

        const buttonsArea = render(`
            <div class="flatpickr-calendar__actions-area">
                <button
                    class="tk-button tk-button--secondary tk-button--size-s"
                    data-select-current-date
                >
                    ${window.opacc.projectDatepickerTexts.today}
                </button>
                <button
                    class="tk-button tk-button--secondary tk-button--size-s"
                    data-reset-date
                >
                    ${window.opacc.projectDatepickerTexts.reset}
                </button>
            </div>
        `);

        this.actionButtons.push(buttonsArea.querySelector('[data-select-current-date]') as HTMLButtonElement);
        this.actionButtons.push(buttonsArea.querySelector('[data-reset-date]') as HTMLButtonElement);

        calendarContainer.appendChild(buttonsArea);
    }

    registerActionButtonsClickListener() {
        if (!this.actionButtons) throw new Error('Missing action buttons');

        const actionButtonClickedHandler = this.onActionButtonClicked.bind(this);

        this.actionButtons.forEach((actionButton) => {
            this.pushListener({ event: 'click', element: actionButton, action: actionButtonClickedHandler });
        });
    }

    onActionButtonClicked(event: MouseEvent | TouchEvent) {
        if ((event.target as HTMLElement).hasAttribute('data-select-current-date')) {
            this.dateRangePicker?.jumpToDate(new Date());
            this.dateRangePicker?.setDate(new Date());
        } else if ((event.target as HTMLElement).hasAttribute('data-reset-date')) {
            this.dateRangePicker?.clear();
        }
    }

    onDocumentClick(event: MouseEvent) {
        if (!this.dateRangePicker) return;

        // Necessary so that the default behavior is suppressed when clicking on the label
        if ((event.target instanceof HTMLLabelElement) && (event.target.closest('tk-form-datepicker'))) {
            event.preventDefault();
        }

        // return if target is form element
        if ((event.target as HTMLElement).closest('tk-form-datepicker')
            || (event.target as HTMLElement).closest('.flatpickr-calendar')) return;

        const { calendarContainer } = this.dateRangePicker;

        // Required to prevent close and open at the same time
        const missingClassClosed = !calendarContainer.classList.contains('open')
            && !calendarContainer.classList.contains('closed');

        if (missingClassClosed) {
            calendarContainer.classList.add('closed');
        }

        if (!calendarContainer.classList.contains('open')) return;
        if (event && event.target && (event.target as HTMLElement).closest('tk-form-datepicker')) return;

        setTimeout(() => {
            calendarContainer.classList.add('closed');
            calendarContainer.classList.remove('open');
        }, 200);
    }

    getAbsolutePosition(element: HTMLElement) {
        // get elements relatove position to viewport
        const { top, left } = element.getBoundingClientRect();

        // get documents scroll position
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        const inputHeight = (this.fieldElement as HTMLElement).offsetHeight;

        const gapDistance = 2;

        // calculate absolute position
        const absoluteLeft = `${left + scrollLeft}px`;
        const tmpAbsoluteTop = `${top + scrollTop + inputHeight + gapDistance}px`;

        const absoluteTopPos = this.checkSpaceUnderCalendar(element, tmpAbsoluteTop, gapDistance);

        return { left: absoluteLeft, top: absoluteTopPos };
    }

    checkSpaceUnderCalendar(element: HTMLElement, currentTopPos: string, gapDistance: number) {
        const { calendarContainer } = this.dateRangePicker!;
        const { calendarHeight } = this;
        const windowHeight = window.innerHeight;
        const elementBoundingRect = element.getBoundingClientRect();
        const elementTopPos = elementBoundingRect.top;
        const elementHeight = elementBoundingRect.height;
        const bottomElementPos = elementTopPos + elementHeight + gapDistance + calendarHeight;
        const elementNotCompletelyVisible = windowHeight < bottomElementPos;

        let newTopPos = currentTopPos;

        if (elementNotCompletelyVisible) {
            const datePickerHeight = this.offsetHeight;
            newTopPos = `${parseInt(currentTopPos, 10) - calendarHeight - (2 * gapDistance) - datePickerHeight}px`;
        }

        calendarContainer.classList.toggle('arrowTop', !elementNotCompletelyVisible);

        return newTopPos;
    }

    getCalendarHeight() {
        if (!this.dateRangePicker) return;

        const { calendarContainer } = this.dateRangePicker;

        if (calendarContainer) {
            calendarContainer.style.visibility = 'hidden';
            calendarContainer.classList.add('open');
            calendarContainer.classList.remove('closed');
            this.calendarHeight = calendarContainer.offsetHeight;
            calendarContainer.classList.remove('open');
            calendarContainer.classList.add('closed');
            calendarContainer.style.visibility = '';
        }
    }
}
